import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search, X, Loader2 } from 'lucide-react';
import Button from '../common/Button/Button';
import customerService from '../../api/crm/customers/customerService';
import { formatIndianAmount } from '../../utils/amountFormatter';
import { toast } from 'react-hot-toast';
import { PAYMENT_METHODS } from '../../constants/paymentMethods';

// Re-export for backward compatibility with existing consumers.
export { PAYMENT_METHODS };

const INVOICE_SEARCH_MAX_LENGTH = 200;
const PAYMENT_REFERENCE_MAX_LENGTH = 200;
const REMARKS_MAX_LENGTH = 1000;

const roundMoney = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return Math.round(num * 100) / 100;
};

const todayInputValue = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Reusable modal to record a customer payment against an invoice.
 *
 * @param {boolean} isOpen
 * @param {string} customerId - Required customer MongoDB id
 * @param {() => void} onClose
 * @param {(payment) => void} [onSuccess] - Called with saved payment data on success
 * @param {string|null} [initialInvoiceId] - Pre-select and lock invoice search
 * @param {string} [title]
 * @param {string} [subtitle]
 * @param {string} [customerName] - Optional label shown under the title
 * @param {string} [submitButtonLabel]
 */
const AddPaymentModal = ({
  isOpen,
  customerId,
  onClose,
  onSuccess,
  initialInvoiceId = null,
  title = 'Add Payment',
  subtitle = 'Record a payment against a customer invoice',
  customerName = null,
  submitButtonLabel = 'Save Payment'
}) => {
  const [invoiceSearch, setInvoiceSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showInvoiceDropdown, setShowInvoiceDropdown] = useState(false);
  const [loadingInitialInvoice, setLoadingInitialInvoice] = useState(false);
  const invoiceSearchRef = useRef(null);

  const [invoiceAmount, setInvoiceAmount] = useState('');
  const [paidAmount, setPaidAmount] = useState('');
  const [outstandingAmount, setOutstandingAmount] = useState('');
  const [lastEditedField, setLastEditedField] = useState(null);

  const [paymentDate, setPaymentDate] = useState(todayInputValue());
  const [paymentMethod, setPaymentMethod] = useState('CASH');
  const [paymentReference, setPaymentReference] = useState('');
  const [remarks, setRemarks] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(invoiceSearch.trim()), 300);
    return () => clearTimeout(timer);
  }, [invoiceSearch]);

  useEffect(() => {
    if (!showInvoiceDropdown) return undefined;

    const handleClickOutside = (event) => {
      if (invoiceSearchRef.current && !invoiceSearchRef.current.contains(event.target)) {
        setShowInvoiceDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showInvoiceDropdown]);

  const isInvoiceLocked = Boolean(initialInvoiceId);

  useEffect(() => {
    if (!isOpen || !customerId || isInvoiceLocked) return undefined;

    let cancelled = false;

    const loadInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const result = await customerService.searchPaymentInvoices(customerId, {
          search: debouncedSearch || undefined,
          page: 1,
          limit: 20
        });

        if (cancelled) return;

        if (result.success) {
          setInvoices(result.invoices || []);
        } else {
          setInvoices([]);
          toast.error(result.error || 'Failed to load invoices');
        }
      } catch (error) {
        if (!cancelled) {
          setInvoices([]);
          toast.error(error.message || 'Failed to load invoices');
        }
      } finally {
        if (!cancelled) setLoadingInvoices(false);
      }
    };

    loadInvoices();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId, debouncedSearch, isInvoiceLocked]);

  useEffect(() => {
    if (!isOpen) {
      setInvoiceSearch('');
      setDebouncedSearch('');
      setInvoices([]);
      setSelectedInvoice(null);
      setShowInvoiceDropdown(false);
      setLoadingInitialInvoice(false);
      setInvoiceAmount('');
      setPaidAmount('');
      setOutstandingAmount('');
      setLastEditedField(null);
      setPaymentDate(todayInputValue());
      setPaymentMethod('CASH');
      setPaymentReference('');
      setRemarks('');
      setErrors({});
      setSubmitting(false);
    }
  }, [isOpen]);

  const applySelectedInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setInvoiceSearch(invoice.invoiceNumber || '');
    setShowInvoiceDropdown(false);
    setInvoiceAmount(String(roundMoney(invoice.invoiceAmount || 0)));
    setPaidAmount('');
    setOutstandingAmount(String(roundMoney(invoice.outstandingAmount || 0)));
    setLastEditedField(null);
    setErrors((prev) => ({ ...prev, invoiceId: undefined }));
  };

  useEffect(() => {
    if (!isOpen || !customerId || !initialInvoiceId) return undefined;

    let cancelled = false;

    const loadInitialInvoice = async () => {
      setLoadingInitialInvoice(true);
      try {
        const result = await customerService.searchPaymentInvoices(customerId, {
          invoiceId: initialInvoiceId,
          page: 1,
          limit: 1
        });

        if (cancelled) return;

        const invoice = result.success ? result.invoices?.[0] : null;
        if (!invoice) {
          toast.error(result.error || 'Invoice not found for payment');
          return;
        }

        if (invoice.isFullyPaid) {
          toast.error('This invoice is already fully paid');
          setInvoiceSearch(invoice.invoiceNumber || '');
          return;
        }

        applySelectedInvoice(invoice);
      } catch (error) {
        if (!cancelled) {
          toast.error(error.message || 'Failed to load invoice for payment');
        }
      } finally {
        if (!cancelled) setLoadingInitialInvoice(false);
      }
    };

    loadInitialInvoice();
    return () => {
      cancelled = true;
    };
  }, [isOpen, customerId, initialInvoiceId]);

  const remainingBeforePayment = useMemo(() => {
    if (!selectedInvoice) return 0;
    return roundMoney(selectedInvoice.outstandingAmount || 0);
  }, [selectedInvoice]);

  const handleSelectInvoice = (invoice) => {
    if (invoice.isFullyPaid) {
      toast.error('This invoice is already fully paid');
      return;
    }

    applySelectedInvoice(invoice);
  };

  const handleClearInvoice = () => {
    setSelectedInvoice(null);
    setInvoiceSearch('');
    setDebouncedSearch('');
    setShowInvoiceDropdown(false);
    setInvoiceAmount('');
    setPaidAmount('');
    setOutstandingAmount('');
    setLastEditedField(null);
    setErrors((prev) => ({ ...prev, invoiceId: undefined, paidAmount: undefined, outstandingAmount: undefined }));
  };

  const applyTwoOfThree = (field, value) => {
    if (!selectedInvoice) return;

    const invoiceAmt = roundMoney(selectedInvoice.invoiceAmount || 0);
    const remaining = roundMoney(selectedInvoice.outstandingAmount || 0);
    const maxPaid = Math.min(remaining, invoiceAmt);

    setLastEditedField(field);

    if (field === 'paid') {
      if (value === '') {
        setPaidAmount('');
        setOutstandingAmount(String(remaining));
        return;
      }

      let paid = roundMoney(value);
      if (!Number.isFinite(paid)) {
        setPaidAmount(value);
        setOutstandingAmount(String(remaining));
        return;
      }

      paid = Math.min(Math.max(0, paid), maxPaid);
      setPaidAmount(String(paid));
      setOutstandingAmount(String(Math.max(0, roundMoney(remaining - paid))));
      return;
    }

    if (field === 'outstanding') {
      if (value === '') {
        setOutstandingAmount('');
        setPaidAmount('');
        return;
      }

      let outstanding = roundMoney(value);
      if (!Number.isFinite(outstanding)) {
        setOutstandingAmount(value);
        setPaidAmount('');
        return;
      }

      outstanding = Math.min(Math.max(0, outstanding), Math.min(remaining, invoiceAmt));
      setOutstandingAmount(String(outstanding));
      setPaidAmount(String(Math.max(0, roundMoney(remaining - outstanding))));
      return;
    }

    setInvoiceAmount(String(invoiceAmt));
    if (lastEditedField === 'paid' && paidAmount !== '') {
      const paid = Math.min(Math.max(0, roundMoney(paidAmount)), maxPaid);
      setPaidAmount(String(paid));
      setOutstandingAmount(String(Math.max(0, roundMoney(remaining - paid))));
    } else if (lastEditedField === 'outstanding' && outstandingAmount !== '') {
      const outstanding = Math.min(Math.max(0, roundMoney(outstandingAmount)), Math.min(remaining, invoiceAmt));
      setOutstandingAmount(String(outstanding));
      setPaidAmount(String(Math.max(0, roundMoney(remaining - outstanding))));
    } else {
      setOutstandingAmount(String(remaining));
      setPaidAmount('');
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!customerId) {
      nextErrors.customerId = 'Customer is required';
    }

    if (!selectedInvoice?._id) {
      nextErrors.invoiceId = 'Please select an invoice';
    }

    const invoiceAmt = roundMoney(selectedInvoice?.invoiceAmount || invoiceAmount || 0);
    const paid = roundMoney(paidAmount);
    const outstanding = roundMoney(outstandingAmount);

    if (paidAmount === '' || !Number.isFinite(paid) || paid <= 0) {
      nextErrors.paidAmount = 'Paid amount must be greater than zero';
    } else if (paid > invoiceAmt + 0.009) {
      nextErrors.paidAmount = `Paid amount cannot exceed invoice amount of ₹${formatIndianAmount(invoiceAmt)}`;
    } else if (paid > remainingBeforePayment + 0.009) {
      nextErrors.paidAmount = `Paid amount cannot exceed outstanding balance of ₹${formatIndianAmount(remainingBeforePayment)}`;
    }

    if (outstandingAmount === '' || !Number.isFinite(outstanding) || outstanding < 0) {
      nextErrors.outstandingAmount = 'Outstanding amount cannot be negative';
    } else if (outstanding > invoiceAmt + 0.009) {
      nextErrors.outstandingAmount = `Outstanding amount cannot exceed invoice amount of ₹${formatIndianAmount(invoiceAmt)}`;
    }

    if (!paymentDate) {
      nextErrors.paymentDate = 'Payment date is required';
    }

    if (!paymentMethod) {
      nextErrors.paymentMethod = 'Payment method is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate() || submitting) return;

    setSubmitting(true);
    try {
      const result = await customerService.addCustomerPayment(customerId, {
        invoiceId: selectedInvoice._id,
        amountReceived: roundMoney(paidAmount),
        outstandingAmount: roundMoney(outstandingAmount),
        paymentDate,
        paymentMethod,
        paymentReference: paymentReference.trim() || undefined,
        remarks: remarks.trim() || undefined,
        source: 'MANUAL'
      });

      if (!result.success) {
        toast.error(result.error || 'Failed to record payment');
        return;
      }

      toast.success(result.message || 'Payment recorded successfully');
      onSuccess?.(result.data);
      onClose?.();
    } catch (error) {
      toast.error(error.message || 'Failed to record payment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <p className="text-sm text-gray-500">{subtitle}</p>
            {customerName && (
              <p className="mt-1 text-sm font-medium text-gray-700">{customerName}</p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="relative" ref={invoiceSearchRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Invoice <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={invoiceSearch}
                maxLength={INVOICE_SEARCH_MAX_LENGTH}
                readOnly={isInvoiceLocked}
                disabled={loadingInitialInvoice || isInvoiceLocked}
                onChange={(e) => {
                  if (isInvoiceLocked) return;
                  const value = e.target.value.slice(0, INVOICE_SEARCH_MAX_LENGTH);
                  setInvoiceSearch(value);
                  setShowInvoiceDropdown(true);
                  if (selectedInvoice && value !== selectedInvoice.invoiceNumber) {
                    setSelectedInvoice(null);
                    setInvoiceAmount('');
                    setPaidAmount('');
                    setOutstandingAmount('');
                  }
                }}
                onFocus={() => {
                  if (!isInvoiceLocked) setShowInvoiceDropdown(true);
                }}
                placeholder={loadingInitialInvoice ? 'Loading invoice...' : 'Search invoice number...'}
                className={`w-full pl-9 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green disabled:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-700 ${
                  (selectedInvoice && !isInvoiceLocked) || loadingInitialInvoice ? 'pr-10' : 'pr-3'
                }`}
              />
              {loadingInitialInvoice ? (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
              ) : selectedInvoice && !isInvoiceLocked ? (
                <button
                  type="button"
                  onClick={handleClearInvoice}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100"
                  aria-label="Clear selected invoice"
                >
                  <X className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            {errors.invoiceId && <p className="mt-1 text-xs text-red-600">{errors.invoiceId}</p>}

            {showInvoiceDropdown && !isInvoiceLocked && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                {loadingInvoices ? (
                  <div className="flex items-center justify-center gap-2 py-4 text-sm text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading invoices...
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="py-4 px-3 text-sm text-gray-500">No invoices found</div>
                ) : (
                  invoices.map((invoice) => (
                    <button
                      key={invoice._id}
                      type="button"
                      onClick={() => handleSelectInvoice(invoice)}
                      disabled={invoice.isFullyPaid}
                      className={`w-full text-left px-3 py-2.5 border-b border-gray-50 last:border-b-0 ${
                        invoice.isFullyPaid
                          ? 'bg-gray-50 text-gray-400 cursor-not-allowed'
                          : 'hover:bg-green-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{invoice.invoiceNumber}</p>
                          <p className="text-xs text-gray-500">
                            Amount: ₹{formatIndianAmount(invoice.invoiceAmount || 0)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs font-medium text-orange-700">
                            Outstanding: ₹{formatIndianAmount(invoice.outstandingAmount || 0)}
                          </p>
                          <p className="text-[10px] uppercase tracking-wide text-gray-500">
                            {invoice.paymentStatus}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Amount</label>
              <input
                type="number"
                value={invoiceAmount}
                readOnly
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Paid Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                max={selectedInvoice ? Math.min(remainingBeforePayment, roundMoney(selectedInvoice.invoiceAmount || 0)) : undefined}
                step="0.01"
                value={paidAmount}
                disabled={!selectedInvoice}
                onChange={(e) => applyTwoOfThree('paid', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green disabled:bg-gray-50"
                placeholder="0.00"
              />
              {errors.paidAmount && <p className="mt-1 text-xs text-red-600">{errors.paidAmount}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Outstanding Amount
              </label>
              <input
                type="number"
                min="0"
                max={selectedInvoice ? Math.min(remainingBeforePayment, roundMoney(selectedInvoice.invoiceAmount || 0)) : undefined}
                step="0.01"
                value={outstandingAmount}
                disabled={!selectedInvoice}
                onChange={(e) => applyTwoOfThree('outstanding', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green disabled:bg-gray-50"
                placeholder="0.00"
              />
              {errors.outstandingAmount && (
                <p className="mt-1 text-xs text-red-600">{errors.outstandingAmount}</p>
              )}
            </div>
          </div>

          {selectedInvoice && (
            <p className="text-xs text-gray-500">
              Current outstanding before this payment: ₹{formatIndianAmount(remainingBeforePayment)}
            </p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                onClick={(e) => {
                  try {
                    e.currentTarget.showPicker?.();
                  } catch {
                    // showPicker can throw if input is not trusted / unsupported
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green cursor-pointer"
              />
              {errors.paymentDate && <p className="mt-1 text-xs text-red-600">{errors.paymentDate}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
              >
                {PAYMENT_METHODS.map((method) => (
                  <option key={method.value} value={method.value}>
                    {method.label}
                  </option>
                ))}
              </select>
              {errors.paymentMethod && (
                <p className="mt-1 text-xs text-red-600">{errors.paymentMethod}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Reference</label>
            <input
              type="text"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value.slice(0, PAYMENT_REFERENCE_MAX_LENGTH))}
              maxLength={PAYMENT_REFERENCE_MAX_LENGTH}
              placeholder="Cheque no. / UTR / Transaction ID"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green"
            />
            <p className="mt-1 text-xs text-gray-400 text-right">
              {paymentReference.length}/{PAYMENT_REFERENCE_MAX_LENGTH}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value.slice(0, REMARKS_MAX_LENGTH))}
              maxLength={REMARKS_MAX_LENGTH}
              rows={3}
              placeholder="Optional notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-agrosaf-green resize-none"
            />
            <p className="mt-1 text-xs text-gray-400 text-right">
              {remarks.length}/{REMARKS_MAX_LENGTH}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-agrosaf-green hover:bg-agrosaf-green-hover text-white"
              disabled={submitting}
            >
              {submitting ? (
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </span>
              ) : (
                submitButtonLabel
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPaymentModal;
